import React, { createContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import Heading from '../../../../../../generic-components/Heading';
import Button from '../../../../../../generic-components/Button';
import { SummaryData } from '../SummaryData/SummaryData';
import { ProductsTable } from '../new/components/ProductsTable';
import { IProduct } from '../../../types/types';
import { CreateNewProductForm } from '../CreateNewProductForm';
import { useUpdateServiceItem } from '../../../hooks/useUpdateServiceItem';
import { useStore } from '../../../../../../store';
import { useCreateNewServiceItem } from '../../../hooks/useCreateNewServiceItem';
import { useFetchServiceItems } from '../../../hooks/useFetchServiceItems';
import { ReactComponent as PlusIcon } from '../../../../../../assets/icons/svg/shared/ic-shared-plus-circle.svg';
import { TEXTS } from '../../../consts/consts';

interface IContext {
  shouldTriggerFocus: boolean;
  mutateTrigger: (status: boolean) => void;
}

export const TableContext = createContext<IContext>({ shouldTriggerFocus: false, mutateTrigger: () => {} });

export const DemiTableWrapper = () => {
  const generateRandomId = () => uuidv4();
  const { t } = useTranslation();
  const emptyItem = {
    amount: 0,
    name: '',
    quantity: 1,
    description: '',
    temporaryId: generateRandomId(),
  };
  const [shouldTriggerFocus, setShouldTriggerFocus] = useState<boolean>(false);
  const [showItemForm, setShowItemForm] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<IProduct | null>(null);
  const localItems = useStore((state) => state.localInvoiceServiceItems);
  const contextValue = useMemo(
    () => ({
      shouldTriggerFocus,
      mutateTrigger: (status: boolean) => setShouldTriggerFocus(status),
    }),
    [shouldTriggerFocus],
  );
  const { getPersonalAccount } = useStore((state) => state);
  const {
    customer: { externalId },
  } = useStore((state) => state.summary);
  const { accountExternalId } = getPersonalAccount();
  const { mutateAsync: mutateAsyncUpdateItem, isLoading: isSavingItem } = useUpdateServiceItem();
  const { mutateAsync: createNewProduct, isLoading: isCreatingNewProduct } = useCreateNewServiceItem();
  const setInvoiceItemsToUpdate = useStore((state) => state.setInvoiceItemsToUpdate);
  const setIsNeedToValidateItems = useStore((state) => state.setIsNeedToValidateItems);
  const [productCatalogList, setProductCatalogList] = useState<IProduct[]>();

  const { refetch: refetchCatalogItems } = useFetchServiceItems({
    customerId: externalId,
    accountExternalId,
    onSuccess: (res) => setProductCatalogList([...res]),
    onError: (err) => {
      console.log(err);
    },
  });
  const updateItemsState = (itemsToSave: IProduct[]) => {
    setInvoiceItemsToUpdate(itemsToSave);
    setIsNeedToValidateItems(false);
  };
  useEffect(() => {
    refetchCatalogItems();
    updateItemsState([emptyItem]);
  }, []);
  const onDeleteClick = (index: number) => {
    const arr = [...localItems];
    const filteredItems = arr.filter((itemX, indexX) => indexX !== index);
    if (filteredItems.length === 0) {
      filteredItems.push(emptyItem);
    }
    updateItemsState(filteredItems);
  };

  const onProductSelected = (product: IProduct, index: number) => {
    const arr = [...localItems];
    const filteredItems = arr.map((productT, indexX) => {
      if (index === indexX) {
        product.quantity = productT.quantity;
        return product;
      }
      return productT;
    });
    updateItemsState(filteredItems);
  };
  const replaceItemByIdAndSaveQuantity = (product: IProduct) => {
    const arr = [...localItems];
    const filteredItems = arr.map((productT) => {
      if (productT.serviceItemId === product.serviceItemId) {
        product.quantity = productT.quantity;
        return product;
      }
      return productT;
    });
    updateItemsState(filteredItems);
  };

  const onAddNewClick = () => {
    const arr = [...localItems];
    const newItem = {
      amount: 0,
      name: '',
      quantity: 1,
      description: '',
      temporaryId: generateRandomId(),
    };
    arr.push(newItem);
    updateItemsState(arr);
  };
  const onEditClick = (index: number) => {
    setSelectedItem(localItems[index]);
    setShowItemForm(true);
  };
  const onCreateNewProductClick = (index: number) => {
    setSelectedItem(localItems[index]);
    setShowItemForm(true);
  };
  const prepareItemData = (data: any) => {
    let amount: number = 0;
    if (typeof data?.amount?.value === 'number') {
      amount = data?.amount?.value;
    } else {
      amount = Number(data?.amount?.value?.replace(/[^0-9.]/g, ''));
    }
    return {
      customerId: externalId,
      accountExternalId,
      serviceItemId: data.serviceItemId || '',
      temporaryId: generateRandomId(),
      payload: {
        name: data.name.value,
        amount,
        description: data.itemDescription.value,
      },
    };
  };
  const handleSaveClick = (data: any) => {
    const variables = prepareItemData(data);
    mutateAsyncUpdateItem(variables).then((updatedProduct) => {
      replaceItemByIdAndSaveQuantity(updatedProduct);
      setShowItemForm(false);
    });
  };

  const onNewProductSubmit = (index: number, data: any) => {
    const variables = prepareItemData(data);
    createNewProduct(variables).then((response) => {
      onProductSelected(response, index);
      setShowItemForm(false);
      refetchCatalogItems();
    });
  };
  const createNewForm = (
    <CreateNewProductForm
      initialName={selectedItem?.name}
      initialItemData={selectedItem}
      isLoading={isSavingItem || isCreatingNewProduct}
      onSubmit={(data) => {
        setSelectedItem(data);
        if (selectedItem?.serviceItemId) {
          handleSaveClick(data);
        } else if (selectedItem) {
          onNewProductSubmit(localItems.indexOf(selectedItem), data);
        }
      }}
      onClose={() => setShowItemForm(false)}
    />
  );
  return (
    <div className='w-full flex flex-col gap-[12px]'>
      <TableContext.Provider value={contextValue}>
        {showItemForm && createNewForm}
        <Heading
          title={t(TEXTS.INVOICE_CREATION.INVOICE_SERVICES_SECTION.TITLE)}
          className='font-metricRMedium text-[16px] text-bw08'
        />
        <ProductsTable
          autocompleteResults={productCatalogList || []}
          products={localItems}
          onDeleteClick={onDeleteClick}
          onEditClick={onEditClick}
          onProductSelected={onProductSelected}
          onCreateNewProductClick={onCreateNewProductClick}
        />
        <div className='flex items-start grow justify-between'>
          <Button
            onClick={onAddNewClick}
            className='w-fit'
            id='elButtonAddLineItem'
            childElement={
              <div className='flex flex-row gap-1 justify-center content-center items-center text-action03 hover:text-action04 '>
                <PlusIcon />
                <Heading
                  title={t(TEXTS.INVOICE_CREATION.INVOICE_SERVICES_SECTION.ADD_BUTTON)}
                  className=' text-[14px] font-metricR'
                />
              </div>
            }
          />
          <SummaryData products={localItems} />
        </div>
      </TableContext.Provider>
    </div>
  );
};
